import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Navigation,
  Printer,
  ArrowRight,
  CornerLeftDown,
  CornerRightDown,
  CornerUpLeft,
  CornerUpRight,
  CircleDot,
  MapPin,
  Clock,
  Car
} from 'lucide-react';
import { TechnicianRoute } from '../types';
import { cn } from '@/lib/utils';

// Route instruction from OSRM
export interface RouteInstruction {
  distance: number; // meters
  time: number; // seconds
  text: string;
  type: string;
  road: string;
  direction: string;
}

// Full route instructions with stops
export interface RouteInstructions {
  instructions: RouteInstruction[];
  totalDistance: number; // miles
  totalTime: number; // minutes
  stops: Array<{
    stopNumber: number;
    name: string;
    address: string;
  }>;
}

interface NavigationDirectionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instructions: RouteInstructions | null;
  route: TechnicianRoute | null;
}

// Get icon for turn type
const getTurnIcon = (type: string, direction: string) => {
  const iconClass = 'h-4 w-4 text-primary';

  // Handle different OSRM instruction types
  const lowerType = type?.toLowerCase() || '';
  const lowerDir = direction?.toLowerCase() || '';

  if (lowerType.includes('left') || lowerDir.includes('left')) {
    if (lowerType.includes('slight')) {
      return <CornerUpLeft className={iconClass} />;
    }
    return <CornerLeftDown className={iconClass} />;
  }

  if (lowerType.includes('right') || lowerDir.includes('right')) {
    if (lowerType.includes('slight')) {
      return <CornerUpRight className={iconClass} />;
    }
    return <CornerRightDown className={iconClass} />;
  }

  if (lowerType.includes('roundabout') || lowerType.includes('rotary')) {
    return <CircleDot className={iconClass} />;
  }

  if (lowerType.includes('arrive') || lowerType.includes('destination')) {
    return <MapPin className={iconClass} />;
  }

  // Default: continue straight
  return <ArrowRight className={iconClass} />;
};

// Format distance for display
const formatDistance = (meters: number): string => {
  const miles = meters * 0.000621371;
  if (miles < 0.1) {
    const feet = Math.round(meters * 3.28084);
    return `${feet} ft`;
  }
  return `${miles.toFixed(1)} mi`;
};

// Generate print HTML
const generatePrintHTML = (
  instructions: RouteInstructions,
  route: TechnicianRoute | null
): string => {
  const technicianName = route?.technician?.full_name || 'Technician';

  const instructionsHTML = instructions.instructions
    .map(
      (inst, i) => `
      <tr>
        <td class="step-num">${i + 1}</td>
        <td class="instruction">${inst.text || 'Continue'}</td>
        <td class="distance">${formatDistance(inst.distance)}</td>
      </tr>
    `
    )
    .join('');

  const stopsHTML = instructions.stops
    .map(
      stop => `
      <li>
        <strong>Stop ${stop.stopNumber}:</strong> ${stop.name}<br/>
        <span class="address">${stop.address}</span>
      </li>
    `
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Turn-by-Turn Directions - ${technicianName}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          color: #333;
          line-height: 1.4;
        }
        h1 {
          color: #d4854a;
          font-size: 24px;
          margin-bottom: 8px;
        }
        .summary {
          background: #f5f5f5;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .summary-item {
          display: inline-block;
          margin-right: 24px;
        }
        .summary-label {
          font-size: 12px;
          color: #666;
        }
        .summary-value {
          font-size: 18px;
          font-weight: bold;
          color: #333;
        }
        h2 {
          font-size: 16px;
          color: #555;
          margin-top: 24px;
          margin-bottom: 12px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 4px;
        }
        .stops-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .stops-list li {
          padding: 8px 0;
          border-bottom: 1px solid #eee;
        }
        .stops-list .address {
          font-size: 12px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 12px;
        }
        th {
          text-align: left;
          padding: 8px;
          background: #f0f0f0;
          border-bottom: 2px solid #ddd;
          font-size: 12px;
        }
        td {
          padding: 8px;
          border-bottom: 1px solid #eee;
          vertical-align: top;
        }
        .step-num {
          width: 40px;
          font-weight: bold;
          color: #d4854a;
        }
        .instruction {
          font-size: 14px;
        }
        .distance {
          width: 80px;
          text-align: right;
          color: #666;
          font-size: 12px;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <h1>Turn-by-Turn Directions</h1>
      <p style="margin-top: 0; color: #666;">Route for ${technicianName}</p>

      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Distance</div>
          <div class="summary-value">${instructions.totalDistance.toFixed(1)} mi</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Estimated Time</div>
          <div class="summary-value">${Math.round(instructions.totalTime)} min</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Stops</div>
          <div class="summary-value">${instructions.stops.length}</div>
        </div>
      </div>

      <h2>Destinations</h2>
      <ul class="stops-list">
        ${stopsHTML}
      </ul>

      <h2>Step-by-Step Directions</h2>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Instruction</th>
            <th style="text-align: right;">Distance</th>
          </tr>
        </thead>
        <tbody>
          ${instructionsHTML}
        </tbody>
      </table>

      <p style="margin-top: 24px; font-size: 11px; color: #999; text-align: center;">
        Generated by Vehix Route Planner
      </p>
    </body>
    </html>
  `;
};

// Print function
const printNavigationDirections = (
  instructions: RouteInstructions,
  route: TechnicianRoute | null
): void => {
  const html = generatePrintHTML(instructions, route);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    // Give the browser time to render before printing
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }
};

export const NavigationDirectionsDialog: React.FC<NavigationDirectionsDialogProps> = ({
  open,
  onOpenChange,
  instructions,
  route
}) => {
  // Filter out very short instructions (less than 10 meters) for cleaner display
  const filteredInstructions = useMemo(() => {
    if (!instructions?.instructions) return [];
    return instructions.instructions.filter(inst => inst.distance >= 10 || inst.type === 'arrive');
  }, [instructions]);

  if (!instructions) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg max-h-[85vh] flex flex-col'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Navigation className='h-5 w-5 text-primary' />
            Turn-by-Turn Directions
          </DialogTitle>
          <DialogDescription>Step-by-step driving directions for your route</DialogDescription>
        </DialogHeader>

        {/* Route Summary */}
        <div className='flex items-center gap-4 p-3 bg-muted rounded-lg'>
          <div className='flex items-center gap-2'>
            <Car className='h-4 w-4 text-muted-foreground' />
            <span className='text-sm font-medium'>{instructions.totalDistance.toFixed(1)} mi</span>
          </div>
          <div className='flex items-center gap-2'>
            <Clock className='h-4 w-4 text-muted-foreground' />
            <span className='text-sm font-medium'>{Math.round(instructions.totalTime)} min</span>
          </div>
          <div className='flex items-center gap-2'>
            <MapPin className='h-4 w-4 text-muted-foreground' />
            <span className='text-sm font-medium'>{instructions.stops.length} stops</span>
          </div>
        </div>

        {/* Destinations */}
        {instructions.stops.length > 0 && (
          <div className='space-y-2'>
            <h4 className='text-sm font-medium text-foreground'>Destinations</h4>
            <div className='space-y-1'>
              {instructions.stops.map(stop => (
                <div
                  key={stop.stopNumber}
                  className='flex items-start gap-2 p-2 bg-muted/50 rounded text-sm'
                >
                  <span
                    className={cn(
                      'flex items-center justify-center w-5 h-5 rounded-full',
                      'bg-primary text-primary-foreground text-xs font-bold flex-shrink-0'
                    )}
                  >
                    {stop.stopNumber}
                  </span>
                  <div className='flex-1 min-w-0'>
                    <div className='font-medium truncate'>{stop.name}</div>
                    <div className='text-xs text-muted-foreground truncate'>{stop.address}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Instructions List */}
        <div className='flex-1 min-h-0'>
          <h4 className='text-sm font-medium text-foreground mb-2'>Directions</h4>
          <ScrollArea className='h-[250px] pr-4'>
            <div className='space-y-1'>
              {filteredInstructions.map((inst, index) => {
                // Create unique key from instruction properties
                const instKey = `${index}-${inst.type}-${inst.distance}-${inst.road || 'road'}`;
                return (
                  <div
                    key={instKey}
                    className={cn(
                      'flex items-start gap-3 p-2 rounded transition-colors',
                      inst.type === 'arrive' ? 'bg-primary/10' : 'hover:bg-muted/50'
                    )}
                  >
                    <div className='flex-shrink-0 w-6 h-6 flex items-center justify-center bg-muted rounded'>
                      {getTurnIcon(inst.type, inst.direction)}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm'>{inst.text || 'Continue'}</p>
                      {inst.road && <p className='text-xs text-muted-foreground'>on {inst.road}</p>}
                    </div>
                    <div className='text-xs text-muted-foreground whitespace-nowrap'>
                      {formatDistance(inst.distance)}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={() => printNavigationDirections(instructions, route)}>
            <Printer className='h-4 w-4 mr-2' />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NavigationDirectionsDialog;
